import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { resolveAuthPageLogoUrl } from '../../../features/auth/helper/auth-page-branding';

/**
 * Componente de diálogo de carga reutilizable
 * 
 * Se utiliza con DialogService de PrimeNG para mostrar
 * un indicador de carga durante operaciones asíncronas
 * 
 * @component
 * @standalone true
 * @example
 * ```typescript
 * const ref = this.dialogService.open(LoadingDialogComponent, {
 *   header: 'Procesando',
 *   data: {
 *     message: 'Actualizando orden de trabajo...',
 *     subtitle: 'Por favor espere',
 *     // logoSrc opcional; si no se envía, se usa branding.logo del environment (mismo criterio que login)
 *   },
 *   closable: false,
 *   modal: true,
 *   styleClass: 'loading-dialog'
 * });
 * 
 * // Cerrar cuando termine
 * ref.close();
 * ```
 */
@Component({
  selector: 'app-loading-dialog',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex flex-col items-center justify-center py-8 px-4">
      <!-- Logo: mismas dimensiones que en login (branding) -->
      <div class="flex justify-center w-full mb-6">
        <img
          [src]="logoSrc"
          [alt]="logoAlt"
          class="h-20 sm:h-24 w-auto max-w-[320px] object-contain animate-pulse"
          width="320"
          height="96"
        />
      </div>

      <!-- Spinner circular -->
      <div class="relative w-16 h-16 mb-6">
        <div class="absolute inset-0 border-4 border-primary-200 dark:border-primary-800 rounded-full"></div>
        <div class="absolute inset-0 border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
      </div>
      
      <!-- Mensaje principal -->
      <p class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2 text-center">
        {{ message }}
      </p>
      
      <!-- Subtítulo opcional -->
      @if (subtitle) {
        <p class="text-sm text-gray-600 dark:text-gray-400 text-center">
          {{ subtitle }}
        </p>
      }
      
      <!-- Mensaje adicional opcional -->
      @if (additionalInfo) {
        <p class="text-xs text-gray-500 dark:text-gray-500 mt-4 text-center max-w-xs">
          {{ additionalInfo }}
        </p>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 300px;
    }
  `]
})
export class LoadingDialogComponent {
  /** Mensaje principal a mostrar */
  message: string = 'Procesando...';

  /** Subtítulo opcional */
  subtitle: string = 'Por favor espere';

  /** Información adicional opcional */
  additionalInfo?: string;

  /** Ruta del logo (por defecto: `environment.branding.logo`, igual que login) */
  logoSrc: string = resolveAuthPageLogoUrl();

  /** Alt del logo */
  logoAlt: string = 'Transmaritime Inc.';

  constructor(public config: DynamicDialogConfig) {
    const brandingLogo = resolveAuthPageLogoUrl();
    if (config.data) {
      this.message = config.data.message || this.message;
      this.subtitle = config.data.subtitle || this.subtitle;
      this.additionalInfo = config.data.additionalInfo;
      this.logoSrc = config.data.logoSrc || brandingLogo;
      this.logoAlt = config.data.logoAlt || this.logoAlt;
    }
  }
}
